import { format, isValid, parseISO, startOfDay } from "date-fns";
import { MarketingResource } from "../types";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export type ResourceFilterOption = {
  key: string;
  label: string;
  count: number;
};

export function getFilterOptions(
  resources: MarketingResource[],
  extractor: (resource: MarketingResource) => string[]
) {
  const counts = new Map<string, number>();

  resources.forEach((resource) => {
    extractor(resource).forEach((value) => {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    });
  });

  return [...counts.entries()]
    .map(([label, count]) => ({
      key: label,
      label,
      count,
    }))
    .sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }

      return left.label.localeCompare(right.label);
    });
}

export function parseResourceDate(value?: string | null) {
  if (!value?.trim()) return null;

  const trimmed = value.trim();
  if (DATE_ONLY_PATTERN.test(trimmed)) {
    const [year, month, day] = trimmed.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return isValid(date) ? date : null;
  }

  const date = trimmed.includes("T") ? parseISO(trimmed) : new Date(trimmed);
  return isValid(date) ? date : null;
}

export function getResourcePreviewImage(resource: MarketingResource) {
  const attachments = Array.isArray(resource.attachments)
    ? resource.attachments
    : [];

  if (!resource.preview_image) {
    return attachments[0]?.attachment ? attachments[0].attachment : "";
  }

  return resource.preview_image;
}

function getYoutubeVideoId(url: string) {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );

  return match?.[1] ?? null;
}

export function getYoutubeEmbedUrl(url: string) {
  const videoId = getYoutubeVideoId(url);

  if (!videoId) {
    return null;
  }

  return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
}

export function getYoutubeThumbnail(url: string) {
  const videoId = getYoutubeVideoId(url);

  if (!videoId) {
    return null;
  }

  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

export function getVideoPreviewImage(resource: MarketingResource) {
  const videoUrl = resource.video_link || resource.link || "";
  return getYoutubeThumbnail(videoUrl) || getResourcePreviewImage(resource);
}

export function isResourceNew(resource: MarketingResource, days = 7) {
  const date =
    resource.resource_type === "webinar"
      ? parseResourceDate(resource.event_date)
      : parseResourceDate(resource.publish_date);
  if (!date) return false;

  const difference = Date.now() - date.getTime();
  return difference >= 0 && difference <= days * 24 * 60 * 60 * 1000;
}

export function formatResourceTitle(title: string) {
  return title
    .replace(/[\r\n\u2028\u2029]+/g, " ")
    .replace(/[ \t]+/g, " ")
    .trim();
}

export function isResourceDateOnOrAfterToday(dateString: string) {
  const date = startOfDay(dateString).getTime();
  if (date === null) return false;

  return date >= startOfDay(new Date()).getTime();
}

export function isResourceDateBeforeToday(dateString: string) {
  const date = startOfDay(dateString).getTime();
  if (date === null) return false;

  return date < startOfDay(new Date()).getTime();
}

export function isResourceDateToday(dateString: string) {
  const date = startOfDay(dateString).getTime();
  if (date === null) return false;

  return date === startOfDay(new Date()).getTime();
}

export function getResourceDate(resource: MarketingResource) {
  return resource.resource_type === "webinar"
    ? resource.event_timings?.[0]?.start_time
    : resource.publish_date;
}

export function formatResourceDate(
  value?: string | null,
  pattern = "dd MMM yyyy"
) {
  const date = parseResourceDate(value);
  return date ? format(date, pattern) : "";
}

export function getWebinarEndTime(resource: MarketingResource) {
  const timings = resource.event_timings ?? [];
  return timings[timings.length - 1]?.end_time ?? null;
}

function formatTimeOfDay(date: Date) {
  return format(date, date.getMinutes() === 0 ? "h a" : "h:mm a").toUpperCase();
}

/** "start time onwards" when there's no end time, or "start time - end time" when there is. */
export function formatWebinarEventTime(resource: MarketingResource) {
  const start = parseResourceDate(getResourceDate(resource));
  if (!start) return "";

  const formattedStart = formatTimeOfDay(start);
  const end = parseResourceDate(getWebinarEndTime(resource));

  return end
    ? `${formattedStart} - ${formatTimeOfDay(end)}`
    : `${formattedStart} onwards`;
}

/** Drops the year token (and its surrounding separator) from a date-fns pattern, e.g. "dd MMM yyyy" -> "dd MMM". */
function stripYearFromPattern(pattern: string) {
  return pattern.replace(/,?\s*y+\s*/g, "").trim();
}

/** "start date" when there's no end time or it falls on the same date, or "start date (no year) - end date" when they differ. */
export function formatWebinarDateRange(resource: MarketingResource) {
  const start = parseResourceDate(getResourceDate(resource));
  if (!start) return "";

  const formattedStart = format(start, "do MMM");
  const end = parseResourceDate(getWebinarEndTime(resource));

  if (!end || format(start, "yyyy-MM-dd") === format(end, "yyyy-MM-dd")) {
    return formattedStart;
  }

  const formattedStartWithoutYear = formatResourceDate(
    getResourceDate(resource),
    stripYearFromPattern("do MMM")
  );

  return `${formattedStartWithoutYear} - ${format(end, "do MMM")}`;
}

export function getNewsletterPages(resource: MarketingResource) {
  const pageCount = resource.metadata?.page_count;
  return typeof pageCount === "number" ? `${pageCount} Pages` : "";
}

function withPartnerUtm(url: string) {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}utm_source=creso-partners`;
}

export function getResourceLink(resource: MarketingResource) {
  const directLink =
    resource.resource_type === "webinar"
      ? resource.video_link || resource.link
      : resource.link;

  if (directLink) return withPartnerUtm(directLink);

  if (["newsletter", "guide"].includes(resource.resource_type)) {
    return (
      resource.attachments.find(
        (attachment) => attachment.attachment_type === "document"
      )?.attachment ??
      resource.attachments[0]?.attachment ??
      ""
    );
  }

  return "";
}

export function getResourceImageAttachments(resource: MarketingResource) {
  const attachments = resource.attachments.filter(
    (attachment) =>
      !["document", "video"].includes(attachment.attachment_type.toLowerCase())
  );

  if (attachments.length > 0) return attachments;

  return resource.preview_image
    ? [
        {
          id: -resource.id,
          attachment: resource.preview_image,
          attachment_type: "image",
          created_at: resource.updated_at,
          updated_at: resource.updated_at,
        },
      ]
    : [];
}

export function sanitizeResourceFileName(title: string, extension: string) {
  const base = title.trim().replace(/[^a-z0-9._-]+/gi, "-") || "resource";
  return `${base}.${extension.replace(/^\./, "")}`;
}

export function hasWebinarRecording(resource: MarketingResource) {
  return Boolean(
    resource.video_link ||
    resource.link?.includes("youtu.be") ||
    resource.link?.includes("youtube.com")
  );
}

export function isUpcomingWebinar(resource: MarketingResource) {
  if (hasWebinarRecording(resource)) return false;
  const date = getResourceDate(resource);
  return Boolean(
    date && startOfDay(date).getTime() >= startOfDay(new Date()).getTime()
  );
}

export function isPastOrRecordedWebinar(resource: MarketingResource) {
  if (hasWebinarRecording(resource)) return true;
  const date = getResourceDate(resource);
  return Boolean(
    date && startOfDay(date).getTime() < startOfDay(new Date()).getTime()
  );
}

export function sortWebinarsByDate(
  resources: MarketingResource[],
  direction: "asc" | "desc"
) {
  const multiplier = direction === "asc" ? 1 : -1;
  return [...resources].sort((left, right) => {
    const leftTime = parseResourceDate(getResourceDate(left))?.getTime() ?? 0;
    const rightTime = parseResourceDate(getResourceDate(right))?.getTime() ?? 0;
    return (leftTime - rightTime) * multiplier;
  });
}

export function getUpcomingWebinars(resources: MarketingResource[]) {
  return sortWebinarsByDate(resources.filter(isUpcomingWebinar), "asc");
}

export function getUpcomingWebinar(resources: MarketingResource[]) {
  return getUpcomingWebinars(resources)[0] ?? null;
}

export function getRecentWebinar(resources: MarketingResource[]) {
  return resources.find(isPastOrRecordedWebinar) ?? null;
}

export function getFeaturedWebinar(resources: MarketingResource[]) {
  return (
    getUpcomingWebinar(resources) ??
    getRecentWebinar(resources) ??
    resources[0] ??
    null
  );
}

export function isWebinarEventPast(resource: MarketingResource) {
  const date = parseResourceDate(getResourceDate(resource));
  return Boolean(date && date.getTime() < Date.now());
}

export function formatVideoDuration(value?: string | number | null) {
  if (value == null || value === "") return "";
  const seconds = Math.floor(Number(value));
  if (!Number.isFinite(seconds) || seconds < 0) return "";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  const pad = (part: number) => String(part).padStart(2, "0");
  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(remainder)}`
    : `${pad(minutes)}:${pad(remainder)}`;
}
